import React, { useState } from 'react'
import styles from './FilesListV2.module.scss'
import { ImageViewer, ImageData } from '../../organismes/ImageViewer'
import { JWayIcon, PdfIcon, ExcelIcon } from '../../../assets/JWayIcons'
import { downloadFile } from '../../../utils/downloader'
import Loader from '../../atoms/Loader/Loader'
import EmptyListIcon from '../../atoms/EmptyListIcon/EmptyListIcon'

export interface FilesListV2Props {
    files?: FileViews[]
    isLoading?: boolean
}

export interface FileViews {
    id: string
    src: string
    name: string
    type: string
    onDelete?: (fileId: string) => void
}

export default function FilesListV2({files, isLoading}: FilesListV2Props) {
    const [viewerOpen, setViewerOpen] = useState(false)
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    
    if (isLoading) {
        return (
            <div className={styles['files-list']}>
                <Loader centered paddingTop="20px" size={40} />
            </div>
        )
    }
    
    if (!files || files.length === 0) {
        return (
            <div className={`${styles['files-list']} ${styles['files-list--empty']}`}>
                <EmptyListIcon message="Aucun fichier disponible" marginTop="20px" size={40} />
            </div>
        )
    }

    // Debug: Log files data
    console.log('FilesListV2 - files:', files)

    const isImageFile = (fileName: string, fileType?: string) => {
        // Vérifier d'abord par l'extension du nom de fichier
        const fileNameLower = fileName.toLowerCase()
        const hasImageExtension = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(fileNameLower)
        
        // Vérifier aussi par le type MIME si disponible
        const mimeType = fileType?.toLowerCase() || ''
        const hasImageMimeType = mimeType.includes('image/') || hasImageExtension
        
        console.log('isImageFile check:', { fileName, fileType, hasImageExtension, hasImageMimeType })
        
        return hasImageExtension || hasImageMimeType
    }

    // Helper function to get file type for display
    const getFileType = (fileName: string, fileType?: string): 'image' | 'pdf' | 'excel' | 'other' => {
        const fileNameLower = fileName.toLowerCase()
        const mimeType = fileType?.toLowerCase() || ''
        
        // Check for images
        if (isImageFile(fileName, fileType)) {
            return 'image'
        }
        
        // Check for PDF
        if (fileNameLower.endsWith('.pdf') || mimeType.includes('pdf')) {
            return 'pdf'
        }
        
        // Check for Excel files
        if (
            fileNameLower.endsWith('.xlsx') || 
            fileNameLower.endsWith('.xls') || 
            fileNameLower.endsWith('.xlsm') || 
            fileNameLower.endsWith('.xlsb') ||
            mimeType.includes('spreadsheet') ||
            mimeType.includes('excel') ||
            mimeType.includes('vnd.ms-excel') ||
            mimeType.includes('vnd.openxmlformats-officedocument.spreadsheetml')
        ) {
            return 'excel'
        }
        
        return 'other'
    }

    // Filter images for the viewer (only image files)
    const imageFiles: ImageData[] = files
        .filter(file => isImageFile(file.name, file.type))
        .map(file => ({
            id: file.id,
            src: file.src,
            name: file.name,
            type: file.type
        }))

    console.log('FilesListV2 - imageFiles:', imageFiles)

    const handleImageClick = (fileId: string) => {
        console.log('handleImageClick called with fileId:', fileId)
        const imageIndex = imageFiles.findIndex(img => img.id === fileId)
        console.log('Found image index:', imageIndex)
        if (imageIndex !== -1) {
            setCurrentImageIndex(imageIndex)
            setViewerOpen(true)
            console.log('Opening viewer with index:', imageIndex)
        }
    }

    const handleDownload = (file: FileViews, event: React.MouseEvent) => {
        event.stopPropagation() // Éviter le conflit avec le clic sur l'image
        console.log('Downloading file:', file.name)
        downloadFile(file.src, file.name, file.type)
    }


    
    return (
        <>
            <div className={styles['files-list']}>
                {files.map((file) => (
                    <div key={file.id} className={styles['file-item']}
                    onClick={() => {
                        console.log('Image container clicked:', file.name, file.type)
                        if (isImageFile(file.name, file.type)) {
                            handleImageClick(file.id)
                        }
                    }}
                    >
                        <div 
                            className={`${styles['file-item__image-container']} ${
                                isImageFile(file.name, file.type) ? styles['file-item__image-container--clickable'] : ''
                            }`}
          
                        >
                            {(() => {
                                const fileType = getFileType(file.name, file.type)
                                
                                if (fileType === 'image') {
                                    return (
                                        <>
                                            <img 
                                                src={file.src} 
                                                alt={file.name}
                                                className={styles['file-item__image']}
                                                onError={(e) => {
                                                    // Fallback en cas d'erreur de chargement d'image
                                                    e.currentTarget.style.display = 'none'
                                                }}
                                            />
                                            <div className={styles['file-item__overlay']}>
                                                <span className={styles['file-item__zoom-icon']}>🔍</span>
                                            </div>
                                        </>
                                    )
                                } else if (fileType === 'pdf') {
                                    return (
                                        <div className={styles['file-item__icon-container']}>
                                            <PdfIcon size={30} />
                                        </div>
                                    )
                                } else if (fileType === 'excel') {
                                    return (
                                        <div className={styles['file-item__icon-container']}>
                                            <ExcelIcon size={30} />
                                        </div>
                                    )
                                } else {
                                    return (
                                        <div className={styles['file-item__icon-container']}>
                                            <div className={styles['file-item__default-icon']}>
                                                📄
                                            </div>
                                        </div>
                                    )
                                }
                            })()}
                        </div>
                        <div className={styles['file-item__content']}>
                            <p className={styles['file-item__name']} title={file.name}>
                                {file.name}
                            </p>
                            <p className={styles['file-item__type']}>
                                {file.type}
                            </p>
                        </div>
                        <div className={styles['file-item__actions']}>
                            <button 
                                className={styles['file-item__download-btn']}
                                onClick={(e) => handleDownload(file, e)}
                                title={`Télécharger ${file.name}`}
                                type="button"
                            >
                             <JWayIcon type="download" size={16} color="#666" />
                            </button>
                            {file.onDelete && (
                                <button 
                                    className={styles['file-item__download-btn']}
                                    onClick={(e) => {e.preventDefault(); e.stopPropagation(); file.onDelete && file.onDelete(file.id)}}
                                    title={`Supprimer ${file.name}`}
                                    type="button"
                                >
                                    <JWayIcon type="trash" size={8} color="#666" />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Image Viewer Modal */}
            {imageFiles.length > 0 && (
                <ImageViewer
                    images={imageFiles}
                    currentIndex={currentImageIndex}
                    isOpen={viewerOpen}
                    onClose={() => {
                        console.log('Closing ImageViewer')
                        setViewerOpen(false)
                    }}
                    onNavigate={setCurrentImageIndex}
                />
            )}
            
        
        </>
    )
}
